import is from '@sindresorhus/is';
import { AnimatePresence } from 'framer-motion';
import i18next from 'i18next';
import type { FunctionComponent, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useWatch } from 'react-hook-form';
import { I18nextProvider } from 'react-i18next';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type {
    JobProfileComponent,
    SectionTagDefinition,
    SectionTitleIdentifier,
} from '~/shared/data-contract/job-profile';
import { getSkillTitle, type JobProfileSkill, type SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';

import type { NavigatableSidepanelItem } from '../../skills';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useSkillLimits } from '../api/use-skill-limits';
import { ContentContainer, ContentSections, FlexRowContainer, StyledStickyHeader, Wrapper } from '../components/Common';
import { CategoryListSkeleton, DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { useIsGeneratingSkills, useIsGeneratingTranslations } from '../hooks/use-is-generating';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { Actions } from './Actions';
import { CommentsClassificationPublisher } from './comments/CommentsClassificationPublisher';
import { CommentsHeaderAction } from './comments/CommentsHeaderAction';
import { sectionTextFromHtml } from './comments/comment-anchor-markup';
import type { CommentDocumentState } from './comments/comment-classification';
import type { AvailableJobItems } from './Details';
import { JobProfileTitle } from './JobProfileTitle';
import { ManualEditStoreProvider } from './ManualEditStoreProvider';
import { NotificationBar } from './notification-bar/NotificationBar';
import { OptionsMenu } from './OptionsMenu';
import type { RegenerationState } from './RegenerateDialog';
import { SidepanelMetadata } from './SidepanelMetadata';
import { SkillsSection } from './SkillsSection';
import { StructuredContentSection } from './StructuredContentSection';
import { useJobProfileForm } from './use-job-profile-form';
import { useTranslatedSectionTitle } from './use-translated-section-title';
import { VersionNoteInput } from './VersionNoteInput';

export type JobProfileCommentableConfig = {
    readonly isOpen: boolean;
    readonly onToggle: () => void;
};

const isAuditExperienceEnabled = (
    itemType: 'job-level' | 'job-role',
    featureFlagService: ReturnType<typeof useApplicationContext>['applicationState']['featureFlagService'],
): boolean => itemType === 'job-role' || featureFlagService.isFeatureFlagEnabled('review-status-table');

const resolveMaximumSkillLimit = (
    itemType: 'job-level' | 'job-role',
    levelMaximumSkillLimit: number | undefined,
    roleMaximumSkillLimit: number | undefined,
): number | undefined => (itemType === 'job-role' ? roleMaximumSkillLimit : levelMaximumSkillLimit);

export type JobProfileContentProps = {
    readonly architectureId: string;
    readonly availableJobItems?: AvailableJobItems;
    readonly breadcrumbs?: ReactNode;
    readonly commentable?: JobProfileCommentableConfig;
    readonly hideActions?: boolean;
    readonly header: ReactNode;
    readonly hideMetadata?: boolean;
    readonly hideNotifications?: boolean;
    readonly isArchived?: boolean;
    readonly isComparingOpen: boolean;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly jobFamilyId?: string;
    readonly onArchive?: () => void;
    readonly onCompareVersions: (versionA: string, versionB: string, preferredLanguage?: LanguageCodeForJSA) => void;
    readonly onDeleteComplete?: () => void;
    readonly onOpenSeparatePage?: () => void;
    readonly onOpenSkillDetails: (
        skillId: string,
        proficiency: SkillProficiencyLevel | null | undefined,
        availableItems: readonly NavigatableSidepanelItem[],
        preferredLanguage?: LanguageCodeForJSA,
    ) => void;
    readonly onRestoreComplete?: () => void;
    readonly onSkillProficiencyChanged?: (skillId: string, level: SkillProficiencyLevel) => void;
    readonly parentRoleId?: string;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly profile: JobProfileComponent;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

type ActionsAndMetadataLayoutProps = {
    readonly actionsNode: ReactNode;
    readonly hasUnsavedChanges: boolean;
    readonly isEditing: boolean;
    readonly isReviewStatusTableEnabled: boolean;
    readonly metadataNode: ReactNode;
    readonly versionNoteInputNode: ReactNode;
};

type NotificationsSlotProps = {
    readonly architectureId: string;
    readonly hideNotifications: boolean;
    readonly isArchived: boolean | undefined;
    readonly isReviewStatusTableEnabled: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly selectedVersion: string | undefined;
    readonly onDeleteComplete: (() => void) | undefined;
    readonly onRestoreComplete: (() => void) | undefined;
};

const ActionsAndMetadataLayout: FunctionComponent<ActionsAndMetadataLayoutProps> = ({
    actionsNode,
    hasUnsavedChanges,
    isEditing,
    isReviewStatusTableEnabled,
    metadataNode,
    versionNoteInputNode,
}) => {
    const shouldShowVersionNoteInput = isEditing && hasUnsavedChanges;
    const integratedPanelClassName = isEditing
        ? shouldShowVersionNoteInput
            ? 'max-h-[10rem]'
            : 'max-h-[3.25rem]'
        : 'gap-xxs';
    const versionNoteSlotClassName = shouldShowVersionNoteInput
        ? 'grid-rows-[1fr] opacity-100 mt-xs'
        : 'grid-rows-[0fr] opacity-0 mt-0 pointer-events-none';

    if (isReviewStatusTableEnabled) {
        return (
            <div className={`mt-12xl ${isEditing ? 'min-h-[6.25rem]' : ''}`}>
                <div
                    className={`flex flex-col overflow-hidden rounded-xl bg-gray-100 p-xxs shadow-sm transition-[max-height] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${integratedPanelClassName}`}
                >
                    {actionsNode}
                    {isEditing ? (
                        <div
                            className={`grid overflow-hidden transition-[grid-template-rows,opacity,margin-top] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${versionNoteSlotClassName}`}
                        >
                            <div className="min-h-0 overflow-hidden">
                                {shouldShowVersionNoteInput ? versionNoteInputNode : null}
                            </div>
                        </div>
                    ) : (
                        metadataNode
                    )}
                </div>
            </div>
        );
    }
    return (
        <>
            {metadataNode}
            {actionsNode}
        </>
    );
};

const NotificationsSlot: FunctionComponent<NotificationsSlotProps> = ({
    architectureId,
    hideNotifications,
    isArchived,
    isReviewStatusTableEnabled,
    itemId,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
    selectedVersion,
}) => {
    if (hideNotifications) {
        return null;
    }

    const notificationBar = (
        <AnimatePresence>
            <NotificationBar
                architectureId={architectureId}
                isArchived={isArchived}
                itemId={itemId}
                itemType={itemType}
                version={selectedVersion}
                onDeleteComplete={onDeleteComplete}
                onRestoreComplete={onRestoreComplete}
            />
        </AnimatePresence>
    );

    if (isReviewStatusTableEnabled) {
        return <div className="not-empty:mt-xxl [&:not(:empty)+*]:mt-xxl!">{notificationBar}</div>;
    }

    return notificationBar;
};

const useCommentDocumentState = ({
    activeLanguage,
    activeLanguageIndex,
    formSkills,
    isEditing,
    profile,
    resolveSectionTitle,
    watchedTranslations,
}: {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly formSkills: readonly JobProfileSkill[];
    readonly isEditing: boolean;
    readonly profile: JobProfileComponent;
    readonly resolveSectionTitle: (section: SectionTitleIdentifier) => string;
    readonly watchedTranslations: JobProfileComponent['translations'] | undefined;
}): CommentDocumentState =>
    useMemo<CommentDocumentState>(() => {
        const structuredItems =
            (isEditing
                ? (watchedTranslations?.[activeLanguageIndex]?.structured ??
                  profile.translations[activeLanguageIndex]?.structured)
                : profile.translations[activeLanguageIndex]?.structured) ?? [];

        return {
            sections: structuredItems.map((structuredItem) => ({
                id: structuredItem.id ?? structuredItem.tag,
                title: resolveSectionTitle(structuredItem),
                text: sectionTextFromHtml(structuredItem.value),
            })),
            skills: formSkills.map((skill) => ({
                id: skill.id,
                title: getSkillTitle(skill, activeLanguage),
            })),
        };
    }, [
        activeLanguage,
        activeLanguageIndex,
        formSkills,
        isEditing,
        profile.translations,
        resolveSectionTitle,
        watchedTranslations,
    ]);

export const JobProfileContentSkeleton: FunctionComponent<{
    readonly breadcrumbs?: ReactNode;
    readonly header: ReactNode;
}> = ({ breadcrumbs, header }) => {
    return (
        <Wrapper style={{ width: '100%' }}>
            <StyledStickyHeader>{header}</StyledStickyHeader>
            {breadcrumbs}
            <TitleSkeleton />
            <ContentContainer>
                <FlexRowContainer>
                    <ContentSections>
                        <div>
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                        </div>
                        <CategoryListSkeleton />
                    </ContentSections>
                </FlexRowContainer>
            </ContentContainer>
        </Wrapper>
    );
};

const JobProfileContentInner: FunctionComponent<JobProfileContentProps> = ({
    architectureId,
    breadcrumbs,
    commentable,
    hideActions = false,
    header,
    hideMetadata = false,
    hideNotifications = false,
    isArchived,
    isComparingOpen,
    isEditing,
    item,
    itemId,
    itemType,
    jobFamilyId,
    onArchive,
    onCompareVersions,
    onDeleteComplete,
    onOpenSeparatePage,
    onOpenSkillDetails,
    onRestoreComplete,
    onSkillProficiencyChanged,
    parentRoleId,
    permissions,
    profile,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}) => {
    const isCommentable = !is.undefined(commentable);
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();
    const isReviewStatusTableEnabled = isAuditExperienceEnabled(itemType, applicationState.featureFlagService);
    const { activeLanguage, activeLanguageIndex } = useActiveLanguage();

    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
    const [regenerationState, setRegenerationState] = useState<RegenerationState>([]);
    const [regeneratedSectionTags, setRegeneratedSectionTags] = useState<readonly SectionTagDefinition[] | undefined>(
        undefined,
    );
    const resolveCommentSectionTitle = useTranslatedSectionTitle(regeneratedSectionTags ?? profile.sectionTags);

    const {
        allCompetencies,
        competenciesWithSkills,
        formMethods,
        formSkills,
        handleCancel,
        handleCancelDiscard,
        handleConfirmDiscard,
        handleManualEdit,
        handleMove,
        handleNewVersion,
        handleRemoveCompetency,
        handleSaveSubmit,
        handleSaveValidationError,
        hasFormChanges,
        isDiscardDialogOpen,
        onChangeProficiencyLevel,
        removeSkill,
        versions,
    } = useJobProfileForm({
        architectureId,
        isEditing,
        item,
        itemId,
        itemType,
        onSkillProficiencyChanged,
        parentRoleId,
        profile,
        regeneratedSectionTags,
        selectedVersion,
        setActiveVersion,
        setIsEditing,
    });
    const watchedTranslations = useWatch({ control: formMethods.control, name: 'translations' });

    const { data: skillLimits } = useSkillLimits({
        architectureId,
        enabled: isEditing,
    });

    const maximumSkillLimit = resolveMaximumSkillLimit(
        itemType,
        skillLimits?.levelMaximumSkillLimit,
        skillLimits?.roleMaximumSkillLimit,
    );

    const isSkillLimitReached = maximumSkillLimit !== undefined && formSkills.length >= maximumSkillLimit;

    const isGeneratingTranslations = useIsGeneratingTranslations(item?.profileGenerationInfo);
    const isGeneratingSkills = useIsGeneratingSkills(item?.profileGenerationInfo);

    const commentDocumentState = useCommentDocumentState({
        activeLanguage,
        activeLanguageIndex,
        formSkills,
        isEditing,
        profile,
        resolveSectionTitle: resolveCommentSectionTitle,
        watchedTranslations,
    });

    const allSkillItems: NavigatableSidepanelItem[] = useMemo(
        () => competenciesWithSkills.flatMap((competency) => competency.skills.map((skill) => ({ id: skill.id }))),
        [competenciesWithSkills],
    );

    useEffect(() => {
        if (!isEditing) {
            setRegeneratedSectionTags(undefined);
            setRegenerationState([]);
        }
    }, [isEditing]);

    const handleOpenSkillDetails = useCallback(
        (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => {
            onOpenSkillDetails(skillId, proficiency, allSkillItems, activeLanguage);
        },
        [activeLanguage, allSkillItems, onOpenSkillDetails],
    );

    const headerOptionsSlot = document.getElementById('header-options-slot');

    const i18n = useMemo(() => {
        const tmp = i18next.cloneInstance();
        tmp.services.languageDetector = null;
        return tmp.cloneInstance({ lng: activeLanguage, react: { useSuspense: false } });
    }, [activeLanguage]);

    const actionsNode = !hideActions ? (
        <Actions
            hasUnsavedChanges={hasFormChanges}
            isArchived={isArchived}
            isComparingOpen={isComparingOpen}
            isEditing={isEditing}
            isIntegrated={isReviewStatusTableEnabled}
            isRegenerateDialogOpen={isRegenerateDialogOpen}
            item={item}
            permissions={permissions}
            regenerateDialogProps={{
                activeLanguage,
                formMethods,
                onSectionTagsRegenerated: setRegeneratedSectionTags,
                profile,
            }}
            selectedVersion={selectedVersion}
            versions={versions ?? []}
            onCancel={handleCancel}
            onNewVersion={handleNewVersion}
            onRegenerateDialogOpenChange={setIsRegenerateDialogOpen}
            onRegenerationStateChange={setRegenerationState}
            onSave={(options) =>
                formMethods.handleSubmit((data) => handleSaveSubmit(data, options), handleSaveValidationError)()
            }
            onVersionChange={(versionId) => {
                setActiveVersion(versionId);
            }}
        />
    ) : null;

    const metadataNode = !hideMetadata ? (
        <SidepanelMetadata
            architectureId={architectureId}
            isArchived={isArchived}
            itemId={itemId}
            itemType={itemType}
            jobFamilyId={jobFamilyId}
            version={selectedVersion}
        />
    ) : null;

    const versionNoteInputNode =
        isEditing && !hideActions ? (
            <VersionNoteInput
                disabled={isGeneratingTranslations || isGeneratingSkills}
                register={formMethods.register('versionNote')}
                onInput={handleManualEdit}
            />
        ) : null;

    const actionsAndMetadata = (
        <ActionsAndMetadataLayout
            actionsNode={actionsNode}
            hasUnsavedChanges={hasFormChanges}
            isEditing={isEditing}
            isReviewStatusTableEnabled={isReviewStatusTableEnabled}
            metadataNode={metadataNode}
            versionNoteInputNode={versionNoteInputNode}
        />
    );

    const content = (
        <>
            {isCommentable ? <CommentsClassificationPublisher documentState={commentDocumentState} /> : null}
            <StyledStickyHeader $paddingBottomToken={isReviewStatusTableEnabled ? 'm' : 'xxl'}>
                {header}
            </StyledStickyHeader>
            {breadcrumbs}
            <NotificationsSlot
                architectureId={architectureId}
                hideNotifications={hideNotifications}
                isArchived={isArchived}
                isReviewStatusTableEnabled={isReviewStatusTableEnabled}
                itemId={itemId}
                itemType={itemType}
                selectedVersion={selectedVersion}
                onDeleteComplete={onDeleteComplete}
                onRestoreComplete={onRestoreComplete}
            />
            <JobProfileTitle
                activeLanguageIndex={activeLanguageIndex}
                architectureId={architectureId}
                control={formMethods.control}
                isCommentable={isCommentable}
                isEditing={isEditing}
                isGeneratingTranslations={isGeneratingTranslations}
                itemId={itemId}
                itemType={itemType}
                register={formMethods.register}
                selectedVersion={selectedVersion}
                onInput={handleManualEdit}
            />
            <ContentContainer>
                <ConfirmationDialog
                    cancelButtonLabel={translate('common.cancel')}
                    confirmButtonLabel={translate('common.confirm')}
                    confirmButtonVariant="default"
                    description={translate('dialogs.discard.content')}
                    open={isDiscardDialogOpen}
                    title={translate('dialogs.discard.title')}
                    onCancel={handleCancelDiscard}
                    onConfirm={handleConfirmDiscard}
                />
                {headerOptionsSlot !== null &&
                    isArchived !== true &&
                    ReactDOM.createPortal(
                        <div className="flex items-center gap-m">
                            {!is.undefined(commentable) ? (
                                <CommentsHeaderAction isOpen={commentable.isOpen} onToggle={commentable.onToggle} />
                            ) : null}
                            <OptionsMenu
                                hasUnsavedChanges={hasFormChanges}
                                isEditing={isEditing}
                                item={item}
                                selectedVersion={selectedVersion}
                                onArchive={onArchive}
                                onCompareVersions={onCompareVersions}
                                onNewVersion={handleNewVersion}
                                onOpenSeparatePage={onOpenSeparatePage}
                                onRegenerate={() => setIsRegenerateDialogOpen(true)}
                                onVersionChange={(versionId) => {
                                    setActiveVersion(versionId);
                                }}
                            />
                        </div>,
                        headerOptionsSlot,
                    )}
                {actionsAndMetadata}
                <FlexRowContainer>
                    <ContentSections>
                        <I18nextProvider i18n={i18n}>
                            <StructuredContentSection
                                activeLanguageIndex={activeLanguageIndex}
                                commentable={isCommentable}
                                control={formMethods.control}
                                isEditing={isEditing}
                                isGeneratingTranslations={isGeneratingTranslations}
                                itemId={itemId}
                                newThreadCreationDisabled={isEditing}
                                overrideSectionTags={regeneratedSectionTags}
                                profile={profile}
                                regenerationState={regenerationState}
                                onUserInput={handleManualEdit}
                            />
                            <SkillsSection
                                allCompetencies={allCompetencies}
                                architectureId={architectureId}
                                commentable={isCommentable}
                                competenciesWithSkills={competenciesWithSkills}
                                currentSkillCount={formSkills.length}
                                formErrors={formMethods.formState.errors}
                                formMethods={formMethods}
                                formSkills={formSkills}
                                hasFormChanges={hasFormChanges}
                                isEditing={isEditing}
                                isGeneratingSkills={isGeneratingSkills}
                                isSkillLimitReached={isSkillLimitReached}
                                itemId={itemId}
                                itemType={itemType}
                                maximumSkillLimit={maximumSkillLimit}
                                newThreadCreationDisabled={false}
                                profile={profile}
                                regenerationState={regenerationState}
                                onChangeProficiencyLevel={onChangeProficiencyLevel}
                                onManualEdit={handleManualEdit}
                                onMove={handleMove}
                                onOpenSkillDetails={handleOpenSkillDetails}
                                onRegenerateDialogOpen={() => setIsRegenerateDialogOpen(true)}
                                onRemoveCompetency={handleRemoveCompetency}
                                onRemoveSkill={removeSkill}
                            />
                        </I18nextProvider>
                    </ContentSections>
                </FlexRowContainer>
            </ContentContainer>
        </>
    );

    return isReviewStatusTableEnabled ? (
        <div className="flex w-full min-w-0 flex-col gap-0" data-anchor-root={itemId}>
            {content}
        </div>
    ) : (
        <Wrapper data-anchor-root={itemId}>{content}</Wrapper>
    );
};

const JobProfileContentImpl: FunctionComponent<JobProfileContentProps> = (props) => {
    const { applicationState } = useApplicationContext();
    const isAiChatEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('ai-chat');
    const hideNotifications = props.hideNotifications === true || isAiChatEnabled;
    return (
        <ManualEditStoreProvider>
            <JobProfileContentInner {...props} hideNotifications={hideNotifications} />
        </ManualEditStoreProvider>
    );
};

export const JobProfileContent = memo(JobProfileContentImpl);
